// Times people pick for scheduled messages, reminders and statuses, and how they read back.

export type TimeChoice = { id: string; label: string; at: Date };

function at(day: Date, hour: number, minute = 0): Date {
  const d = new Date(day);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function addDays(day: Date, n: number): Date {
  const d = new Date(day);
  d.setDate(d.getDate() + n);
  return d;
}

/** The next Monday after today. */
function nextMonday(now: Date): Date {
  const days = (8 - now.getDay()) % 7 || 7;
  return addDays(now, days);
}

/** Drops a choice that falls at the same minute as one before it, as Monday does on a Sunday. */
function distinct<T extends { at: Date | null }>(list: T[]): T[] {
  const seen = new Set<number>();
  return list.filter((c) => {
    if (!c.at) return true;
    const t = Math.floor(c.at.getTime() / 60_000);
    if (seen.has(t)) return false;
    seen.add(t);
    return true;
  });
}

/** "Mon 09:00", the short day and time a choice falls on. */
export function shortWhen(d: Date): string {
  const day = d.toLocaleDateString(undefined, { weekday: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time}`;
}

/** Choices for "Send later". */
export function sendChoices(now = new Date()): TimeChoice[] {
  const tomorrow = addDays(now, 1);
  const list: TimeChoice[] = [];
  if (now.getHours() < 17) list.push({ id: "evening", label: "This evening", at: at(now, 18) });
  list.push({ id: "tomorrow", label: "Tomorrow morning", at: at(tomorrow, 9) });
  list.push({ id: "monday", label: "Monday morning", at: at(nextMonday(now), 9) });
  return distinct(list);
}

/** Choices for "Remind me". */
export function remindChoices(now = new Date()): TimeChoice[] {
  return distinct([
    { id: "20m", label: "In 20 minutes", at: new Date(now.getTime() + 20 * 60_000) },
    { id: "1h", label: "In 1 hour", at: new Date(now.getTime() + 60 * 60_000) },
    { id: "3h", label: "In 3 hours", at: new Date(now.getTime() + 3 * 60 * 60_000) },
    { id: "tomorrow", label: "Tomorrow morning", at: at(addDays(now, 1), 9) },
    { id: "monday", label: "Monday morning", at: at(nextMonday(now), 9) },
  ]);
}

/** Choices for when a status clears. Null keeps it until it is changed. */
export function clearChoices(now = new Date()): { id: string; label: string; at: Date | null }[] {
  return [
    { id: "30m", label: "In 30 minutes", at: new Date(now.getTime() + 30 * 60_000) },
    { id: "1h", label: "In 1 hour", at: new Date(now.getTime() + 60 * 60_000) },
    { id: "4h", label: "In 4 hours", at: new Date(now.getTime() + 4 * 60 * 60_000) },
    { id: "today", label: "At the end of today", at: at(now, 23, 59) },
    { id: "week", label: "At the end of this week", at: at(addDays(now, (7 - now.getDay()) % 7), 23, 59) },
    { id: "never", label: "Do not clear", at: null },
  ];
}

/** "Today at 18:00", "Tomorrow at 09:00", "Monday at 09:00" or "3 Nov at 09:00". */
export function whenLabel(iso: string | Date, now = new Date()): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(now)) / 86_400_000);
  if (diff === 0) return `Today at ${time}`;
  if (diff === 1) return `Tomorrow at ${time}`;
  if (diff === -1) return `Yesterday at ${time}`;
  if (diff > 1 && diff < 7) return `${d.toLocaleDateString(undefined, { weekday: "long" })} at ${time}`;
  const date = d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${date} at ${time}`;
}

/** The value a datetime-local input takes for a date, in local time. */
export function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
